import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Building,
  Users,
  Trophy,
  Target,
  TrendingUp,
  Award,
  ArrowLeft,
  Presentation,
  Medal,
  CheckCircle,
  Clock,
  BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepartmentProfile() {
  const { department } = useParams();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDepartmentData();
  }, [department]);

  const fetchDepartmentData = async () => {
    try {
      const [usersRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/sessions`)
      ]);
      
      const deptUsers = usersRes.data.filter(u => u.department === decodeURIComponent(department));
      setUsers(deptUsers);
      
      const userIds = deptUsers.map(u => u.id);
      const deptSessions = sessionsRes.data.filter(s => userIds.includes(s.user_id));
      setSessions(deptSessions);
    } catch (error) {
      console.error("Failed to fetch department data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalColor = (index) => {
    switch(index) {
      case 0: return "text-[#FFD700]";
      case 1: return "text-[#C0C0C0]";
      case 2: return "text-[#CD7F32]";
      default: return "text-slate-400";
    }
  };

  const decodedDept = decodeURIComponent(department);
  const totalSessions = sessions.length;
  const completedSessions = sessions.filter(s => s.status === 'analyzed' || s.status === 'completed').length;
  const avgScore = users.length > 0 
    ? Math.round(users.reduce((sum, u) => sum + (u.average_score || 0), 0) / users.length)
    : 0;
  const topPerformers = [...users].sort((a, b) => (b.average_score || 0) - (a.average_score || 0));
  const currentMonth = new Date().toISOString().slice(0, 7);
  const compliantUsers = users.filter(u => 
    sessions.some(s => s.user_id === u.id && s.month === currentMonth && (s.status === 'analyzed' || s.status === 'completed'))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin">
          <Button variant="ghost" size="icon" className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {decodedDept}
          </h1>
          <p className="text-slate-500">Department Overview</p>
        </div>
      </div>

      {/* Stats Grid - Clickable Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="border-0 shadow-lg bg-gradient-to-br from-primary to-teal-600 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          onClick={() => document.getElementById('team-members')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>{users.length}</p>
            <p className="text-sm opacity-80">Team Members</p>
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          onClick={() => document.getElementById('top-performers')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <CardContent className="p-6 text-center">
            <Award className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>{avgScore}</p>
            <p className="text-sm opacity-80">Avg Score</p>
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          onClick={() => document.getElementById('recent-sessions')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <CardContent className="p-6 text-center">
            <Presentation className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>{totalSessions}</p>
            <p className="text-sm opacity-80">Total Sessions</p>
          </CardContent>
        </Card>

        <Link to="/admin/compliance">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all h-full">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>{compliantUsers.length}/{users.length}</p>
              <p className="text-sm opacity-80">Compliant</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Performers */}
        <Card id="top-performers" className="border-0 shadow-xl scroll-mt-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Trophy className="w-5 h-5 text-amber-500" />
              Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {topPerformers.slice(0, 5).map((user, index) => (
              <Link key={user.id} to={`/admin/users/${user.id}`}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    {index < 3 ? (
                      <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                    ) : (
                      <span className="text-sm font-bold text-slate-400">#{index + 1}</span>
                    )}
                  </div>
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-primary text-white text-sm font-semibold">
                      {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate text-sm">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.sessions_completed || 0} sessions</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary text-lg">{user.average_score || 0}</p>
                  </div>
                </div>
              </Link>
            ))}
            {topPerformers.length === 0 && (
              <p className="text-center text-slate-500 py-8">No team members yet</p>
            )}
          </CardContent>
        </Card>

        {/* All Team Members */}
        <Card id="team-members" className="lg:col-span-2 border-0 shadow-xl scroll-mt-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Users className="w-5 h-5 text-primary" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {users.map((user) => {
                const userSessions = sessions.filter(s => s.user_id === user.id);
                const hasCompletedThisMonth = userSessions.some(s => 
                  s.month === currentMonth && (s.status === 'analyzed' || s.status === 'completed')
                );
                
                return (
                  <Link key={user.id} to={`/admin/users/${user.id}`}>
                    <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer border border-slate-100">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-primary text-white font-semibold">
                          {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                      <div className="text-center px-4">
                        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                          {user.sessions_completed || 0}
                        </p>
                        <p className="text-xs text-slate-500">Sessions</p>
                      </div>
                      <div className="text-center px-4">
                        <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>
                          {user.average_score || 0}
                        </p>
                        <p className="text-xs text-slate-500">Avg Score</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        hasCompletedThisMonth 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {hasCompletedThisMonth ? 'Compliant' : 'Pending'}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions */}
      <Card id="recent-sessions" className="border-0 shadow-xl scroll-mt-24">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Presentation className="w-5 h-5 text-primary" />
            Recent Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {sessions.slice(0, 10).map((session) => {
              const sessionUser = users.find(u => u.id === session.user_id);
              return (
                <Link key={session.id} to={`/session/${session.id}/feedback`}>
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer border border-slate-100">
                    <div className={`w-2 h-12 rounded-full ${
                      session.status === 'analyzed' ? 'bg-emerald-500' :
                      session.status === 'completed' ? 'bg-blue-500' : 'bg-slate-300'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{session.topic_name}</p>
                      <p className="text-sm text-slate-500">by {sessionUser?.name || 'Unknown'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        {new Date(session.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {session.overall_score && (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                        session.overall_score >= 80 ? 'bg-emerald-500' :
                        session.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {session.overall_score}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
            {sessions.length === 0 && (
              <p className="text-center text-slate-500 py-8">No sessions yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
